import { DumpIcon } from "../components/DumpIcon";
import { createEffect, createSignal, For, on, Show } from "solid-js";
import { apiRequest } from "../core/api";
import { currentUser, hasPermission } from "../core/session";
import { CrudCollectionPage, type CrudRecord } from "./CrudCollectionPage";

export function StaffPage() {
  return (
    <CrudCollectionPage
      label="Profiles"
      endpoint="/users"
      icon="nav_staff"
      canCreate={hasPermission("config.users.create")}
      canUpdate={hasPermission("config.users.update")}
      canDelete={hasPermission("config.users.delete")}
      advancedSearch={false}
      createDefaults={{
        characterId: null,
        steamId: null,
        firstName: "",
        lastName: "",
        alias: "",
        userType: "police",
        callsign: "",
        phoneNumber: "",
        profilePicture: null,
        isActive: true,
      }}
      fields={[
        { key: "characterId", label: "State ID", type: "number" },
        { key: "steamId", label: "Steam ID" },
        { key: "firstName", label: "First Name" },
        { key: "lastName", label: "Last Name" },
        { key: "alias", label: "Alias" },
        { key: "callsign", label: "Callsign" },
        { key: "phoneNumber", label: "Phone Number" },
        { key: "profilePicture", label: "Profile Picture" },
        { key: "userType", label: "User Type" },
      ]}
      itemTitle={(item) =>
        `${value(item.callsign)} - ${value(item.firstName)} ${value(item.lastName)}`
      }
      itemDescription={(item) => value(item.userType)}
      itemAvatar={(item) => value(item.profilePicture) || null}
      prepareWrite={(item) => ({
        characterId: Number(item.characterId),
        steamId: nullableText(item.steamId),
        firstName: value(item.firstName),
        lastName: value(item.lastName),
        alias: value(item.alias),
        userType: value(item.userType) || "police",
        callsign: value(item.callsign),
        phoneNumber: value(item.phoneNumber),
        profilePicture: nullableText(item.profilePicture),
        isActive: item.isActive !== false,
      })}
      renderAfterFields={(item) => <StaffRelations item={item} />}
      actions={
        <button class="flex h-7 cursor-pointer items-center justify-center bg-[rgba(1,137,102,.25)] px-2 hover:bg-[rgba(1,137,102,.75)]">
          <DumpIcon name="nav_reports" />
          <p class="pl-2">Callsign Matrix</p>
        </button>
      }
    />
  );
}

function StaffRelations(props: { item: CrudRecord }) {
  const [strikes, setStrikes] = createSignal<CrudRecord[]>([]);
  const [allDepartments, setAllDepartments] = createSignal<CrudRecord[]>([]);
  const [allRanks, setAllRanks] = createSignal<CrudRecord[]>([]);
  const [departmentIds, setDepartmentIds] = createSignal<number[]>([]);
  const [rankIds, setRankIds] = createSignal<number[]>([]);
  const [reason, setReason] = createSignal("");
  const [savingMemberships, setSavingMemberships] = createSignal(false);
  const id = () => Number(props.item.id);
  const departments = () =>
    Array.isArray(props.item.departments)
      ? (props.item.departments as CrudRecord[])
      : [];
  const ranks = () =>
    Array.isArray(props.item.ranks) ? (props.item.ranks as CrudRecord[]) : [];
  const certs = () =>
    Array.isArray(props.item.certs) ? (props.item.certs as CrudRecord[]) : [];
  const load = async () => {
    if (!id()) return;
    const token = currentUser()?.accessToken;
    const [strikeResult, departmentResult, rankResult] = await Promise.all([
      apiRequest<CrudRecord[]>(`/users/${id()}/strikes`, { token }).catch(
        () => [],
      ),
      apiRequest<CrudRecord[]>("/departments", { token }).catch(() => []),
      apiRequest<CrudRecord[]>("/ranks", { token }).catch(() => []),
    ]);
    setStrikes(strikeResult);
    setAllDepartments(departmentResult);
    setAllRanks(rankResult);
    setDepartmentIds(
      departments()
        .map((item) => Number(item.id))
        .filter((departmentId) => Number.isSafeInteger(departmentId)),
    );
    setRankIds(
      ranks()
        .map((item) => Number(item.id))
        .filter((rankId) => Number.isSafeInteger(rankId)),
    );
  };
  createEffect(on(id, () => void load(), { defer: false }));

  const toggle = (
    current: number[],
    selectedId: number,
    checked: boolean,
  ) =>
    checked
      ? [...new Set([...current, selectedId])]
      : current.filter((id) => id !== selectedId);

  const saveMemberships = async () => {
    setSavingMemberships(true);
    const token = currentUser()?.accessToken;
    try {
      await Promise.all([
        apiRequest(`/users/${id()}/departments`, {
          method: "PATCH",
          token,
          body: {
            departments: departmentIds().map((departmentId) => ({
              departmentId,
            })),
          },
        }),
        apiRequest(`/users/${id()}/ranks`, {
          method: "PATCH",
          token,
          body: { ranks: rankIds().map((rankId) => ({ rankId })) },
        }),
      ]);
    } finally {
      setSavingMemberships(false);
    }
  };
  const addStrike = async () => {
    if (!reason().trim()) return;
    await apiRequest(`/users/${id()}/strikes`, {
      method: "POST",
      token: currentUser()?.accessToken,
      body: { reason: reason(), points: 1 },
    });
    setReason("");
    await load();
  };
  const removeStrike = async (strikeId: unknown) => {
    await apiRequest(`/users/${id()}/strikes/${String(strikeId)}`, {
      method: "DELETE",
      token: currentUser()?.accessToken,
    });
    await load();
  };
  return (
    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <section class="bg-white-5 p-3">
        <p class="mb-2 roboto500">Departments, Ranks & Certifications</p>
        <p class="mb-1 text-xs uppercase text-white-35">Departments</p>
        <div class="mb-3 grid grid-cols-2 gap-2">
          <For each={allDepartments()}>
            {(department) => (
              <label class="flex cursor-pointer items-center gap-2 bg-gray-gradient px-2 py-2 text-sm">
                <input
                  type="checkbox"
                  checked={departmentIds().includes(Number(department.id))}
                  onChange={(event) =>
                    setDepartmentIds((current) =>
                      toggle(
                        current,
                        Number(department.id),
                        event.currentTarget.checked,
                      ),
                    )
                  }
                />
                {value(department.shortName) || value(department.name)}
              </label>
            )}
          </For>
        </div>
        <p class="mb-1 text-xs uppercase text-white-35">Ranks</p>
        <div class="mb-3 grid grid-cols-2 gap-2">
          <For each={allRanks()}>
            {(rank) => (
              <label class="flex cursor-pointer items-center gap-2 bg-gray-gradient px-2 py-2 text-sm">
                <input
                  type="checkbox"
                  checked={rankIds().includes(Number(rank.id))}
                  onChange={(event) =>
                    setRankIds((current) =>
                      toggle(
                        current,
                        Number(rank.id),
                        event.currentTarget.checked,
                      ),
                    )
                  }
                />
                {value(rank.name)}
              </label>
            )}
          </For>
        </div>
        <button
          class="mb-3 bg-green-gradient px-4 py-2 disabled:opacity-40"
          disabled={savingMemberships()}
          onClick={() => void saveMemberships()}
        >
          Save Assignments
        </button>
        <p class="text-sm text-white-55">
          Certifications: {certs().map((item) => value(item.name)).join(", ") || "None"}
        </p>
      </section>
      <section class="bg-white-5 p-3">
        <p class="mb-2 roboto500">Disciplinary Action Points</p>
        <div class="mb-2 flex gap-2">
          <input
            class="h-8 flex-1 bg-white-11 px-2"
            placeholder="Reason"
            value={reason()}
            onInput={(event) => setReason(event.currentTarget.value)}
          />
          <button class="bg-green-gradient px-3" onClick={() => void addStrike()}>
            Add DAP
          </button>
        </div>
        <Show when={strikes().length} fallback={<p class="text-sm text-white-35">No DAP records.</p>}>
          <For each={strikes()}>
            {(strike) => (
              <div class="mb-2 flex items-center justify-between bg-gray-gradient px-3 py-2">
                <div>
                  <p>{value(strike.reason)}</p>
                  <p class="text-xs text-white-55">{value(strike.points)} point(s)</p>
                </div>
                <button
                  class="flex h-7 w-7 items-center justify-center bg-red-gradient"
                  onClick={() => void removeStrike(strike.id)}
                >
                  <DumpIcon name="tab_close" />
                </button>
              </div>
            )}
          </For>
        </Show>
      </section>
    </div>
  );
}

function value(input: unknown): string {
  return typeof input === "string" || typeof input === "number"
    ? String(input)
    : "";
}
function nullableText(input: unknown): string | null {
  const output = value(input).trim();
  return output ? output : null;
}
